import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Alert,
  CircularProgress,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Menu,
  ListItemText,
  Snackbar,
  styled,
  alpha,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  AdminPanelSettings as AdminIcon,
  AccountCircle as ClerkIcon,
  CheckCircle as CheckCircleIcon,
  Engineering as EngineeringIcon,
  CopyAll as CopyAllIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { collection, query, where, getDocs, updateDoc, doc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { User, Site, COLLECTIONS, convertTimestamp } from '../../../types/firebase';
import { useAuth } from '../../../contexts/AuthContext';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { createUserWithAuth, deleteUserWithAuth } from '../../../services/userService';
import { validateUserLimit, UserLimitValidationResult } from '../../../services/subscriptionService';

// Styled components - use theme glass; only add layout
const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease',
}));

const RoleChip = styled(Chip)<{ role: string }>(({ role, theme }) => ({
  fontWeight: 600,
  borderRadius: '50px',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  color: '#ffffff',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  backdropFilter: 'blur(12px)',
  '& .MuiChip-label': { px: 1.5 },
}));

const StatusChip = styled(Chip)<{ status: string }>(({ status, theme }) => ({
  fontWeight: 600,
  borderRadius: '50px',
  backgroundColor: 'rgba(255, 255, 255, 0.08)',
  color: status === 'active' ? '#22d3ee' : 'rgba(255,255,255,0.7)',
  border: '1px solid rgba(255, 255, 255, 0.12)',
  backdropFilter: 'blur(12px)',
  '& .MuiChip-label': { px: 1.5 },
}));

const SearchBar = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: theme.palette.background.paper,
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
}));

interface UserModalProps {
  open: boolean;
  onClose: () => void;
  user?: User | null;
  onSave: (userData: Partial<User>) => Promise<void>;
  sites: Site[];
}

const UserModal: React.FC<UserModalProps> = ({ open, onClose, user, onSave, sites }) => {
  const [formData, setFormData] = useState<Partial<User>>({
    name: '',
    email: '',
    role: 'Clerk',
    assignedSites: [],
    defaultSweepTeamNumber: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        role: user.role,
        assignedSites: user.assignedSites || [],
        defaultSweepTeamNumber:
          typeof user.defaultSweepTeamNumber === 'number' && Number.isFinite(user.defaultSweepTeamNumber)
            ? Math.round(user.defaultSweepTeamNumber)
            : null,
      });
    } else {
      setFormData({
        name: '',
        email: '',
        role: 'Clerk',
        assignedSites: [],
        defaultSweepTeamNumber: null,
      });
    }
  }, [user]);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save user. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const dialogPaperSx = {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(34, 211, 238, 0.25)',
    borderTop: '1px solid rgba(34, 211, 238, 0.5)',
    borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
    borderRadius: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
  };
  const dialogTitleSx = {
    fontFamily: "'Inter', sans-serif",
    color: '#ffffff',
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '0.3em',
    textTransform: 'uppercase' as const,
    padding: '20px 24px',
    borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
    backgroundColor: 'transparent',
  };
  const dialogActionsSx = {
    padding: '16px 24px',
    gap: 1,
    borderTop: '1px solid rgba(34, 211, 238, 0.2)',
    backgroundColor: 'transparent',
  };
  const pillButtonCancel = {
    color: '#ffffff',
    border: '1px solid rgba(255, 255, 255, 0.15)',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '50px',
    fontWeight: 'bold',
    px: 2,
    '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
  };
  const pillButtonPrimary = {
    color: '#ffffff',
    border: '1px solid rgba(34, 211, 238, 0.5)',
    background: 'rgba(34, 211, 238, 0.15)',
    borderRadius: '50px',
    fontWeight: 'bold',
    px: 2,
    '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={dialogTitleSx}>
        {user ? 'Edit User' : 'Add New User'}
      </DialogTitle>
      <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              variant="outlined"
            />
          </Box>
          <Box sx={{ width: { xs: '100%', md: '50%' } }}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
                label="Role"
              >
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Tech">Tech</MenuItem>
                <MenuItem value="Clerk">Clerk</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <TextField
            fullWidth
            label="Default sweep team #"
            type="number"
            inputProps={{ min: 0, step: 1 }}
            value={
              formData.defaultSweepTeamNumber != null && Number.isFinite(formData.defaultSweepTeamNumber)
                ? String(formData.defaultSweepTeamNumber)
                : ''
            }
            onChange={(e) => {
              const t = e.target.value.trim();
              if (t === '') {
                setFormData({ ...formData, defaultSweepTeamNumber: null });
                return;
              }
              const n = parseInt(t, 10);
              setFormData({
                ...formData,
                defaultSweepTeamNumber: Number.isFinite(n) ? Math.round(n) : null,
              });
            }}
            helperText="Optional. Used in Sweeps until this user saves their own team on a device. Leave blank for no default."
            variant="outlined"
          />
          {formData.role === 'Clerk' && (
              <FormControl fullWidth>
                <InputLabel>Assigned Sites</InputLabel>
                <Select
                  multiple
                  value={formData.assignedSites || []}
                  onChange={(e) => {
                    const value = e.target.value as string[];
                    
                    // Check if "select-all" was clicked
                    if (value.includes('select-all')) {
                      const allSiteIds = sites.map(site => site.id);
                      const currentSelection = formData.assignedSites || [];
                      const isAllSelected = allSiteIds.length > 0 && allSiteIds.every(id => currentSelection.includes(id));
                      
                      // Toggle: if all selected, deselect all; otherwise select all
                      setFormData({ 
                        ...formData, 
                        assignedSites: isAllSelected ? [] : allSiteIds 
                      });
                    } else {
                      // Normal selection behavior for individual sites
                      setFormData({ ...formData, assignedSites: value });
                    }
                  }}
                  label="Assigned Sites"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((siteId) => {
                        const site = sites.find((s) => s.id === siteId);
                        return (
                          <Chip
                            key={siteId}
                            label={site?.name || siteId}
                            size="small"
                            onDelete={() => {
                              // Remove this site from selection when chip is clicked
                              const newSelection = (formData.assignedSites || []).filter(id => id !== siteId);
                              setFormData({ ...formData, assignedSites: newSelection });
                            }}
                            onMouseDown={(e) => {
                              e.stopPropagation(); // Prevent dropdown from opening when clicking chip
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {/* Select All Option */}
                  <MenuItem
                    value="select-all"
                    sx={{
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      marginBottom: 1,
                      fontWeight: 'bold'
                    }}
                  >
                    <Checkbox 
                      checked={sites.length > 0 && sites.every(site => (formData.assignedSites || []).includes(site.id))}
                      indeterminate={
                        sites.some(site => (formData.assignedSites || []).includes(site.id)) &&
                        !sites.every(site => (formData.assignedSites || []).includes(site.id))
                      }
                    />
                    <ListItemText primary="Select All Sites" />
                  </MenuItem>
                  
                  {/* Individual Site Options */}
                  {sites.map((site) => (
                    <MenuItem key={site.id} value={site.id}>
                      <Checkbox checked={(formData.assignedSites || []).indexOf(site.id) > -1} />
                      <ListItemText primary={site.name} secondary={site.address} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={dialogActionsSx}>
        <Button onClick={onClose} disabled={loading} variant="outlined" sx={pillButtonCancel}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="outlined"
          disabled={loading || !formData.name || !formData.email}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          sx={{
            ...pillButtonPrimary,
            '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.05)' },
          }}
        >
          {user ? 'Update' : 'Create'} User
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UsersTab: React.FC = () => {
  const { currentUser, userProfile, updateUserProfile } = useAuth();
  const { colors } = useTheme();
  const muiTheme = useMuiTheme();
  const [users, setUsers] = useState<User[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [credentialsDialogOpen, setCredentialsDialogOpen] = useState(false);
  const [createdUserCredentials, setCreatedUserCredentials] = useState<{ email: string; password: string; name: string } | null>(null);
  /** Shown when SendGrid/Pages email API failed (user was still created) */
  const [credentialsEmailWarning, setCredentialsEmailWarning] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuUser, setSelectedMenuUser] = useState<User | null>(null);
  const [userLimitValidation, setUserLimitValidation] = useState<UserLimitValidationResult | null>(null);
  const [checkingLimits, setCheckingLimits] = useState(false);
  const isAdmin = userProfile?.role === 'Admin';

  useEffect(() => {
    if (currentUser && userProfile) {
      fetchUsers();
      fetchSites();
      checkUserLimits();
    }
  }, [currentUser, userProfile]);

  const checkUserLimits = async () => {
    if (!userProfile?.masterOwnerID) return;
    
    setCheckingLimits(true);
    try {
      const validation = await validateUserLimit(userProfile.masterOwnerID, userProfile.masterOwnerID);
      setUserLimitValidation(validation);
    } catch (error) {
      console.error('Error checking user limits:', error);
    } finally {
      setCheckingLimits(false);
    }
  };

  const fetchUsers = async () => {
    if (!currentUser || !userProfile) return;

    setLoading(true);
    try {
      let q;
      if (userProfile.role === 'Admin') {
        // Admins see all users in their organization
        q = query(
          collection(db, COLLECTIONS.USERS),
          where('masterOwnerID', '==', userProfile.masterOwnerID),
          orderBy('createdAt', 'desc')
        );
      } else if (userProfile.role === 'Tech') {
        // Techs see users in their organization
        q = query(
          collection(db, COLLECTIONS.USERS),
          where('masterOwnerID', '==', userProfile.masterOwnerID),
          orderBy('createdAt', 'desc')
        );
      } else {
        // Clerks only see themselves
        q = query(
          collection(db, COLLECTIONS.USERS),
          where('ownerID', '==', currentUser.uid)
        );
      }

      const querySnapshot = await getDocs(q);
      const fetchedUsers: User[] = [];

      querySnapshot.forEach((doc) => {
        fetchedUsers.push({
          ...doc.data(),
          ownerID: doc.id,
        } as User);
      });

      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    if (!userProfile) return;

    try {
      const q = query(
        collection(db, COLLECTIONS.SITES),
        where('masterOwnerID', '==', userProfile.masterOwnerID),
        orderBy('name')
      );

      const querySnapshot = await getDocs(q);
      const fetchedSites: Site[] = [];

      querySnapshot.forEach((doc) => {
        fetchedSites.push({
          ...doc.data(),
          id: doc.id,
        } as Site);
      });

      setSites(fetchedSites);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const handleSaveUser = async (userData: Partial<User>) => {
    if (!currentUser || !userProfile) return;
    if (!isAdmin) {
      throw new Error('Only admins can manage users.');
    }

    // Check user limits before creating a new user
    if (!selectedUser && userLimitValidation && !userLimitValidation.canCreateUser) {
      throw new Error(userLimitValidation.errorMessage || 'User limit exceeded');
    }

    try {
      if (selectedUser) {
        const role = (userData.role ?? selectedUser.role) as User['role'];
        const assignedSites: string[] =
          role === 'Clerk'
            ? Array.isArray(userData.assignedSites)
              ? userData.assignedSites
              : selectedUser.assignedSites ?? []
            : [];
        const nextDef =
          userData.defaultSweepTeamNumber != null && Number.isFinite(userData.defaultSweepTeamNumber)
            ? Math.round(userData.defaultSweepTeamNumber as number)
            : null;
        await updateDoc(doc(db, COLLECTIONS.USERS, selectedUser.ownerID), {
          name: userData.name ?? selectedUser.name,
          email: userData.email ?? selectedUser.email,
          role,
          assignedSites,
          defaultSweepTeamNumber: nextDef,
          updatedAt: Timestamp.now(),
        });
        if (currentUser?.uid && selectedUser.ownerID === currentUser.uid) {
          updateUserProfile({ defaultSweepTeamNumber: nextDef });
        }
      } else {
        // Create new user with Firebase Auth
        console.log('Creating user with Firebase Auth:', userData);
        const newDef =
          userData.defaultSweepTeamNumber != null && Number.isFinite(userData.defaultSweepTeamNumber)
            ? Math.round(userData.defaultSweepTeamNumber as number)
            : null;
        const result = await createUserWithAuth({
          name: userData.name || '',
          email: userData.email || '',
          role: userData.role || 'Clerk',
          masterOwnerID: userProfile.masterOwnerID,
          ownerID: '', // This will be set by auth
          assignedSites: userData.assignedSites || [],
          announcements: userData.role === 'Admin' ? true : false,
          notificationTimeZone: 'America/New_York', // Default timezone
          defaultSweepTeamNumber: newDef,
        });

        if (!result.success) {
          throw new Error(result.error || 'Failed to create user');
        }

        console.log('User created successfully with password:', result.password);
        setCredentialsEmailWarning(result.emailError ?? null);
        setCreatedUserCredentials({
          email: userData.email || '',
          password: result.password || '',
          name: userData.name || '',
        });
        setCredentialsDialogOpen(true);
      }
      await fetchUsers();
      await checkUserLimits();
      setModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error saving user:', error);
      throw error;
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    if (!isAdmin) return;

    try {
      const result = await deleteUserWithAuth(userToDelete.ownerID, userToDelete.email);
      if (result.success) {
        console.log('User deleted successfully:', result.message);
        await fetchUsers();
        setDeleteDialogOpen(false);
        setUserToDelete(null);
      } else {
        console.error('Error deleting user:', result.error);
        // Still refresh the list in case partial deletion occurred
        await fetchUsers();
        setDeleteDialogOpen(false);
        setUserToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      // Still refresh the list in case partial deletion occurred
      await fetchUsers();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setAnchorEl(event.currentTarget);
    setSelectedMenuUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMenuUser(null);
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const formatUserCreatedAt = (value: User['createdAt'] | undefined) => {
    if (value == null) return 'N/A';
    const d = convertTimestamp(value);
    return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Admin':
        return <AdminIcon />;
      case 'Tech':
        return <EngineeringIcon />;
      default:
        return <ClerkIcon />;
    }
  };

  const canManageUsers = isAdmin;

  const glassCardSx = {
    background: 'rgba(255, 255, 255, 0.03)',
    backgroundImage: 'linear-gradient(135deg, rgba(34, 211, 238, 0.05) 0%, transparent 100%)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderTop: '1px solid rgba(255, 255, 255, 0.15)',
    borderRadius: '20px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05)',
  };

  return (
    <>
    <Card className="card-4" sx={{ marginBottom: 3 }}>
      <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Typography
          component="span"
          sx={{
            fontFamily: "'Inter', sans-serif",
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
          }}
        >
          User Management
        </Typography>
        {canManageUsers && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedUser(null);
              setModalOpen(true);
            }}
            disabled={userLimitValidation ? !userLimitValidation.canCreateUser : false}
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(34, 211, 238, 0.5)',
              background: 'rgba(34, 211, 238, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
              '&:disabled': { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.05)' },
            }}
          >
            Add User
          </Button>
        )}
      </Box>

      {/* User Limit Alert */}
      {userLimitValidation && (
        <Alert 
          severity={userLimitValidation.canCreateUser ? "info" : "warning"} 
          sx={{ mb: 2 }}
        >
          <Typography variant="body2">
            Users: {userLimitValidation.currentUserCount}/{userLimitValidation.isUnlimited ? 'Custom' : userLimitValidation.maxUsers}
            {!userLimitValidation.isUnlimited && userLimitValidation.canCreateUser && (
              <span> ({userLimitValidation.maxUsers - userLimitValidation.currentUserCount} remaining)</span>
            )}
            {!userLimitValidation.canCreateUser && (
              <span> - {userLimitValidation.errorMessage}</span>
            )}
          </Typography>
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ flex: 2 }}>
          <SearchBar
            fullWidth
            placeholder="Search users by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <FormControl fullWidth>
            <InputLabel>Filter by Role</InputLabel>
            <Select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              label="Filter by Role"
            >
              <MenuItem value="all">All Roles</MenuItem>
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Tech">Tech</MenuItem>
              <MenuItem value="Clerk">Clerk</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* User Statistics */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
        <Card sx={glassCardSx}>
          <CardContent>
            <Typography color="text.secondary" variant="body2">Total Users</Typography>
            <Typography variant="h4" color="text.primary" fontWeight="bold">{users.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ ...glassCardSx, borderColor: 'rgba(244, 67, 54, 0.2)', backgroundImage: 'linear-gradient(135deg, rgba(244, 67, 54, 0.05) 0%, transparent 100%)' }}>
          <CardContent>
            <Typography color="text.secondary" variant="body2">Admins</Typography>
            <Typography variant="h4" sx={{ color: '#f44336' }} fontWeight="bold">{users.filter((u) => u.role === 'Admin').length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ ...glassCardSx, borderColor: 'rgba(255, 152, 0, 0.2)', backgroundImage: 'linear-gradient(135deg, rgba(255, 152, 0, 0.05) 0%, transparent 100%)' }}>
          <CardContent>
            <Typography color="text.secondary" variant="body2">Techs</Typography>
            <Typography variant="h4" sx={{ color: '#ff9800' }} fontWeight="bold">{users.filter((u) => u.role === 'Tech').length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ ...glassCardSx, borderColor: 'rgba(33, 150, 243, 0.2)', backgroundImage: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, transparent 100%)' }}>
          <CardContent>
            <Typography color="text.secondary" variant="body2">Clerks</Typography>
            <Typography variant="h4" sx={{ color: '#2196f3' }} fontWeight="bold">{users.filter((u) => u.role === 'Clerk').length}</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Users Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.2)',
            borderRadius: '20px',
            overflow: 'hidden',
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Def. sweep team</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned Sites</TableCell>
                <TableCell>Created</TableCell>
                {canManageUsers && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <TableRow key={user.ownerID} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ bgcolor: muiTheme.palette.primary.main }}>
                          {user.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography variant="body1" fontWeight={500}>
                          {user.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <RoleChip role={user.role} label={user.role} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {typeof user.defaultSweepTeamNumber === 'number' && Number.isFinite(user.defaultSweepTeamNumber)
                          ? Math.round(user.defaultSweepTeamNumber)
                          : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status="active" label="Active" size="small" />
                    </TableCell>
                    <TableCell>
                      {user.role === 'Clerk' && user.assignedSites ? (
                        <Box display="flex" gap={0.5} flexWrap="wrap">
                          {user.assignedSites.slice(0, 2).map((siteId) => {
                            const site = sites.find((s) => s.id === siteId);
                            return (
                              <Chip
                                key={siteId}
                                label={site?.name || siteId}
                                size="small"
                                sx={{
                                  borderRadius: '50px',
                                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                  color: '#ffffff',
                                  border: '1px solid rgba(255, 255, 255, 0.12)',
                                  '& .MuiChip-label': { px: 1.25 },
                                }}
                              />
                            );
                          })}
                          {user.assignedSites.length > 2 && (
                            <Chip
                              label={`+${user.assignedSites.length - 2} more`}
                              size="small"
                              sx={{
                                borderRadius: '50px',
                                backgroundColor: 'rgba(255, 255, 255, 0.08)',
                                color: '#ffffff',
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                '& .MuiChip-label': { px: 1.25 },
                              }}
                            />
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {user.role === 'Admin' || user.role === 'Tech'
                            ? 'All Sites'
                            : 'None'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatUserCreatedAt(user.createdAt)}
                      </Typography>
                    </TableCell>
                    {canManageUsers && (
                      <TableCell align="right">
                        <IconButton
                          onClick={(e) => handleMenuClick(e, user)}
                          aria-label="User actions"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredUsers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TableContainer>
      )}

      </CardContent>
    </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            setSelectedUser(selectedMenuUser);
            setModalOpen(true);
            handleMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setUserToDelete(selectedMenuUser);
            setDeleteDialogOpen(true);
            handleMenuClose();
          }}
          disabled={selectedMenuUser?.ownerID === currentUser?.uid}
          sx={{ color: muiTheme.palette.error.main }}
        >
          <DeleteIcon sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* User Modal */}
      <UserModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
        onSave={handleSaveUser}
        sites={sites}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            borderTop: '1px solid rgba(34, 211, 238, 0.5)',
            borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Inter', sans-serif",
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
            backgroundColor: 'transparent',
          }}
        >
          Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will delete the user from the database and attempt to remove their Firebase Auth account.
          </Alert>
          <Typography>
            Are you sure you want to delete user "{userToDelete?.name}"? This action cannot be
            undone and will permanently remove their access to the system.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', gap: 1, borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(255, 255, 255, 0.2)', borderColor: 'rgba(255, 255, 255, 0.25)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(244, 67, 54, 0.5)',
              background: 'rgba(244, 67, 54, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(244, 67, 54, 0.3)', borderColor: 'rgba(244, 67, 54, 0.7)' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credentials Dialog */}
      <Dialog
        open={credentialsDialogOpen}
        onClose={() => {
          setCredentialsDialogOpen(false);
          setCreatedUserCredentials(null);
          setCredentialsEmailWarning(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(34, 211, 238, 0.25)',
            borderTop: '1px solid rgba(34, 211, 238, 0.5)',
            borderLeft: '1px solid rgba(34, 211, 238, 0.4)',
            borderRadius: '20px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 1px rgba(255, 255, 255, 0.05), inset 0 0 20px rgba(34, 211, 238, 0.05), 0 0 15px rgba(34, 211, 238, 0.1)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "'Inter', sans-serif",
            color: '#ffffff',
            fontSize: '15px',
            fontWeight: 700,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            padding: '20px 24px',
            borderBottom: '1px solid rgba(34, 211, 238, 0.2)',
            backgroundColor: 'transparent',
          }}
        >
          User Created Successfully
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent', padding: '24px' }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            The user account has been created successfully. Please securely share these credentials:
          </Alert>

          {credentialsEmailWarning && (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Automatic email was not sent: {credentialsEmailWarning}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="User Name"
              value={createdUserCredentials?.name || ''}
              InputProps={{ readOnly: true }}
              variant="outlined"
              sx={{ '& input': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' } }}
            />

            <TextField
              fullWidth
              label="Email Address"
              value={createdUserCredentials?.email || ''}
              type="text"
              variant="outlined"
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={async () => {
                        if (createdUserCredentials?.email) {
                          try {
                            await navigator.clipboard.writeText(createdUserCredentials.email);
                            setSnackbarMessage('Email copied to clipboard!');
                            setSnackbarOpen(true);
                          } catch {
                            setSnackbarMessage('Failed to copy email');
                            setSnackbarOpen(true);
                          }
                        }
                      }}
                      edge="end"
                    >
                      <CopyAllIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Generated Password"
              value={createdUserCredentials?.password || ''}
              type="text"
              variant="outlined"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={async () => {
                        if (createdUserCredentials?.password) {
                          try {
                            await navigator.clipboard.writeText(createdUserCredentials.password);
                            setSnackbarMessage('Password copied to clipboard!');
                            setSnackbarOpen(true);
                          } catch (err) {
                            setSnackbarMessage('Failed to copy password');
                            setSnackbarOpen(true);
                          }
                        }
                      }}
                      edge="end"
                    >
                      <CopyAllIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                readOnly: true,
              }}
              sx={{ '& input': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' } }}
            />

            {createdUserCredentials ? (
              <TextField
                fullWidth
                multiline
                minRows={5}
                label="Copy / paste block"
                value={[
                  `Name: ${createdUserCredentials.name}`,
                  `Login email: ${createdUserCredentials.email}`,
                  `Password: ${createdUserCredentials.password}`,
                  '',
                  'Change your password after first login.',
                ].join('\n')}
                InputProps={{ readOnly: true }}
                variant="outlined"
                onFocus={(e) => {
                  const t = e.target as HTMLTextAreaElement;
                  t?.select?.();
                }}
                sx={{
                  '& textarea': {
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: '0.875rem',
                    userSelect: 'all',
                  },
                }}
              />
            ) : null}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 3 }}>
            <Button
              variant="outlined"
              startIcon={<CopyAllIcon />}
              onClick={async () => {
                if (createdUserCredentials) {
                  const credentials = `Name: ${createdUserCredentials.name}\nEmail: ${createdUserCredentials.email}\nPassword: ${createdUserCredentials.password}`;
                  try {
                    await navigator.clipboard.writeText(credentials);
                    setSnackbarMessage('All credentials copied to clipboard!');
                    setSnackbarOpen(true);
                  } catch (err) {
                    setSnackbarMessage('Failed to copy credentials');
                    setSnackbarOpen(true);
                  }
                }
              }}
              size="small"
            >
              Copy All
            </Button>

            <Button
              variant="contained"
              startIcon={<EmailIcon />}
              onClick={() => {
                if (createdUserCredentials) {
                  const subject = 'Your New Account Credentials';
                  const body = `Hello ${createdUserCredentials.name},

Your account has been created successfully!

Email: ${createdUserCredentials.email}
Password: ${createdUserCredentials.password}

Please change your password after first login for security.

Best regards,
SkimBuster Team`;

                  const mailtoLink = `mailto:${createdUserCredentials.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                  window.open(mailtoLink);
                }
              }}
              size="small"
              color="primary"
            >
              Send Email
            </Button>

            <Button
              variant="contained"
              startIcon={<PhoneIcon />}
              onClick={async () => {
                if (createdUserCredentials) {
                  const smsText = `Your SkimBuster account:\nName: ${createdUserCredentials.name}\nEmail: ${createdUserCredentials.email}\nPassword: ${createdUserCredentials.password}\nChange password after login.`;
                  try {
                    await navigator.clipboard.writeText(smsText);
                    setSnackbarMessage('SMS text copied! Paste into your SMS app');
                    setSnackbarOpen(true);
                  } catch (err) {
                    setSnackbarMessage('Failed to copy SMS text');
                    setSnackbarOpen(true);
                  }
                }
              }}
              size="small"
              color="secondary"
            >
              Copy for SMS
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px', borderTop: '1px solid rgba(34, 211, 238, 0.2)', backgroundColor: 'transparent' }}>
          <Button
            onClick={() => {
              setCredentialsDialogOpen(false);
              setCreatedUserCredentials(null);
            }}
            variant="outlined"
            sx={{
              color: '#ffffff',
              border: '1px solid rgba(34, 211, 238, 0.5)',
              background: 'rgba(34, 211, 238, 0.15)',
              borderRadius: '50px',
              fontWeight: 'bold',
              px: 2,
              '&:hover': { background: 'rgba(34, 211, 238, 0.25)', borderColor: '#22d3ee' },
            }}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for copy feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </>
  );
};

export default UsersTab;
